import { useMemo } from 'react';

import { extractOppgaveIdFromPathname, extractOppgaveReturUrlFraPathname } from '@components/oppgave/utils/oppgave-url-utils';
import { PageRoutes } from '@src/types/types';
import { useAppSelector } from '@store';
import { BreadcrumbText } from '@utils/constants';
import { extractVerktoyIdFromPathname } from '@utils/verktoyContainer-utils';
import { getQueryURLFromState } from '@utils/verktoyFilters';
import { useLocation } from 'react-router';

import type { VerktoyFilterState } from '@src/types/stateTypes';

import type { BreadcrumbLink } from '@helsenorge/core-framework/breadcrumb';

import { useGetResources } from './useGetResources';

const buildQueryString = (verktoyFilterState: VerktoyFilterState): string => {
  if (!verktoyFilterState) {
    return '';
  }

  const queryParamPart = getQueryURLFromState(false, verktoyFilterState);
  const params = new URLSearchParams();

  Object.entries(queryParamPart).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(val => params.append(key, val));
    } else {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

const detectOppgaveContext = (): { isFromOppgave: boolean; retururl: string } => {
  const oppgaveId = extractOppgaveIdFromPathname();
  const retururl = extractOppgaveReturUrlFraPathname();
  return {
    isFromOppgave: !!(oppgaveId && retururl),
    retururl,
  };
};

const resolveOrigin = (fromRouterState: string | undefined) => fromRouterState || PageRoutes.entryPage;

const getReturnUrl = (pathname: string, fromRouterState: string | undefined, oppgaveRetururl: string): string => {
  const origin = resolveOrigin(fromRouterState);
  const verktoyId = extractVerktoyIdFromPathname(pathname, 1);
  const pathSuffix = verktoyId ? `/${verktoyId}` : '';

  switch (origin) {
    case PageRoutes.finnVerktoy:
      return `${PageRoutes.finnVerktoy}${pathSuffix}`;
    case PageRoutes.entryPage:
      return PageRoutes.entryPage;
    case PageRoutes.oppgave:
      return oppgaveRetururl ? `${window.location.origin}${oppgaveRetururl}` : PageRoutes.finnVerktoy;
    default:
      return PageRoutes.finnVerktoy;
  }
};

const getBreadcrumbText = (fromRouterState: string | undefined): string => {
  const origin = resolveOrigin(fromRouterState);

  switch (origin) {
    case PageRoutes.finnVerktoy:
      return BreadcrumbText.FINN_VERKTOY;
    case PageRoutes.oppgave:
      return BreadcrumbText.INNBOKS;
    default:
      return BreadcrumbText.ENTRY_PAGE;
  }
};

export const useBreadcrumb = (): BreadcrumbLink | undefined => {
  const location = useLocation();
  const resources = useGetResources();
  const verktoyFilters = useAppSelector(state => state.verktoy.verktoyFilters);
  const error = useAppSelector(state => state.verktoy.error);

  return useMemo(() => {
    if (!resources) {
      return undefined;
    }

    const pathname = location.pathname;
    const { isFromOppgave, retururl } = detectOppgaveContext();
    const fromRouterState = location.state?.from as string | undefined;

    if (pathname === PageRoutes.entryPage) {
      return undefined;
    }

    if (pathname.includes(PageRoutes.finnVerktoy)) {
      return {
        url: PageRoutes.entryPage,
        name: resources.entryPage_header || 'Verktøy',
        renderAsRouterLink: true,
      };
    }

    if (isFromOppgave) {
      return {
        url: `${window.location.origin}${retururl}`,
        name: resources.innboks_breadcrumb,
        renderAsRouterLink: false,
      };
    }

    let returnUrl = getReturnUrl(pathname, fromRouterState, retururl);

    if (returnUrl.includes(PageRoutes.finnVerktoy) && !error) {
      returnUrl += buildQueryString(verktoyFilters);
    }

    const origin = resolveOrigin(fromRouterState);

    // When there's an error and coming from oppgave, preserve the oppgave return URL
    const errorUrl =
      error && origin === PageRoutes.oppgave && retururl
        ? `${window.location.origin}${retururl}`
        : `${PageRoutes.baseUrl}${PageRoutes.finnVerktoy}`;

    return {
      url: error ? errorUrl : returnUrl,
      name: getBreadcrumbText(fromRouterState),
      renderAsRouterLink: !error && origin !== PageRoutes.oppgave,
    };
  }, [location, verktoyFilters, resources, error]);
};
